import React from 'react';
import { Input, CustomSelect } from '../../shared';
import { DatePicker, TimePicker } from '../../client';
import useOperatingMarkets from '../../../hooks/useOperatingMarkets';

const EventDetailsForm = ({
  bookingData,
  errors,
  onInputChange,
  requiredFields = [],
}) => {
  const { options: countryOptions, isLoading: marketsLoading, isEmpty } = useOperatingMarkets({
    bookableOnly: true,
  });
  const req = (field) => requiredFields.includes(field);

  return (
    <div>
      <h2 className="text-xl sm:text-2xl font-bold text-black mb-6 sm:mb-8">
        Event Details
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
        <div>
          <DatePicker
            label="Event Date"
            value={bookingData.eventDate}
            onChange={(value) => onInputChange('eventDate', value)}
            placeholder="Select event date"
            minDate={(() => {
              // API requires after:today — same-day events are not allowed
              const d = new Date();
              d.setHours(0, 0, 0, 0);
              d.setDate(d.getDate() + 1);
              const y = d.getFullYear();
              const m = String(d.getMonth() + 1).padStart(2, '0');
              const day = String(d.getDate()).padStart(2, '0');
              return `${y}-${m}-${day}`;
            })()}
            allowPastDates={false}
            error={errors.eventDate}
            required
            className="w-full"
          />
        </div>
        <div>
          <TimePicker
            label="Event Time *"
            value={bookingData.eventTime}
            onChange={(value) => onInputChange('eventTime', value)}
            placeholder="Select event time"
            format="24"
            step={15}
            required
            className={`w-full ${errors.eventTime ? 'border-red-500' : ''}`}
          />
          {errors.eventTime && (
            <p className="mt-1 text-sm text-red-600">{errors.eventTime}</p>
          )}
        </div>
        <div>
          <Input
            label="Guest Count"
            type="number"
            value={bookingData.guestCount}
            onChange={(e) => onInputChange('guestCount', e.target.value)}
            min="1"
          />
        </div>
        <div>
          <Input
            label="Event Type *"
            type="text"
            value={bookingData.eventType}
            onChange={(e) => onInputChange('eventType', e.target.value)}
            placeholder="e.g., Wedding, Birthday, Corporate"
            required
            error={errors.eventType || ''}
          />
        </div>
        <div className="md:col-span-2">
          <Input
            label={req('event_address') || req('event_location') ? 'Event Location *' : 'Event Location'}
            type="text"
            value={bookingData.location}
            onChange={(e) => onInputChange('location', e.target.value)}
            placeholder="Enter full event address"
            error={errors.location || ''}
          />
        </div>
        <div>
          <CustomSelect
            label={req('event_country') ? 'Event market *' : 'Event market'}
            value={bookingData.eventCountry || ''}
            onChange={(value) => onInputChange('eventCountry', value)}
            options={countryOptions}
            placeholder={marketsLoading ? 'Loading markets…' : 'Select market'}
            error={errors.eventCountry || (isEmpty ? 'No markets are open for bookings' : '')}
            disabled={marketsLoading || isEmpty}
            size="md"
          />
          <p className="mt-1 text-xs text-gray-500">
            Only markets the marketplace operates in.
          </p>
        </div>
        <div className="md:col-span-2">
          <label className="block text-sm font-semibold text-black mb-2">
            Special Requests
          </label>
          <textarea
            className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-black transition-colors text-sm"
            rows="4"
            value={bookingData.specialRequests}
            onChange={(e) => onInputChange('specialRequests', e.target.value)}
            placeholder="Any special requirements or requests..."
          />
        </div>
      </div>
    </div>
  );
};

export default EventDetailsForm;
